import { useCallback, useEffect, useRef } from 'react';

import useToast from '@/hooks/useToast';
import useTourney from '@/hooks/useTourney';
import { challengeUser, requestTratarDesafio } from '@/services/challenges';
import { ChallengeUser } from '@/types/challenges';
import { ActionsTourneys, GenericItemTourneysData } from '@/types/tda';
import { TEXTS } from '@/utils';

export const useHandleInvitations = (
  list: GenericItemTourneysData[] | null
) => {
  const toast = useToast();
  const { updateTourney, tourneyData } = useTourney();
  const listRef = useRef<GenericItemTourneysData[] | null>(null);

  // Agregar ref para matchDay actual
  const currentMatchDayRef = useRef(tourneyData?.matchDay);

  // Update the ref when list changes
  useEffect(() => {
    if (!list) return;
    listRef.current = list;
  }, [list]);

  // Actualizar ref cuando cambie matchDay
  useEffect(() => {
    currentMatchDayRef.current = tourneyData?.matchDay;
  }, [tourneyData?.matchDay]);

  const onHandlePendingPostulation = useCallback(
    async (id: number) => {
      const challenge = (
        tourneyData.tourneysInvitations as ChallengeUser[]
      ).find((t) => t.idUsuarioDesafiador === id);
      try {
        const responseManagePostulant = await requestTratarDesafio(
          challenge?.idDesafio || 0,
          'aceptar'
        );

        if (responseManagePostulant.estado) {
          toast.show(`Tu desafío quedó confirmado.`);
        } else {
          console.error(responseManagePostulant.mensaje);
          toast.show('Error al invitar');
        }
      } catch (error) {
        console.error(error);
      }
    },
    [toast, tourneyData.tourneysInvitations]
  );

  const onHandleSubmit = useCallback(
    async ({ id }: ActionsTourneys) => {
      try {
        // Check if list is available
        if (!listRef.current) return;

        const tourney = listRef.current.find((t) => t.id === id);
        if (!tourney) return; // Add check for tourney existence

        const response = await challengeUser(id);

        if (response.estado) {
          toast.show(`Desafiaste a ${tourney?.title || tourney?.name}`);
          // set refresh and pending list ids
          updateTourney({
            ...tourneyData,
            idsPendings: [...(tourneyData.idsPendings || []), id],
            matchDay: currentMatchDayRef?.current,
          });
        } else {
          if (
            response?.mensaje &&
            response?.mensaje.startsWith(
              'Ya existe un desafío entre los usuarios.'
            )
          ) {
            await onHandlePendingPostulation(id);

            // set refresh after invitacion
            updateTourney({
              ...tourneyData,
              idsPlayings: [...(tourneyData.idsPlayings || []), id],
            });
          } else if (response.mensaje) {
            toast.show(response.mensaje);
          }
        }
      } catch (err) {
        console.error(err);
        toast.show(TEXTS.defaultError);
      }
    },
    [toast, updateTourney, tourneyData, onHandlePendingPostulation]
  );

  return { onHandleSubmit };
};
